/**
 * bills.watched_amendments — dashboard widget for big-bill (NDAA-style)
 * trackers. Shows every amendment the workspace has starred on any bill,
 * with current status. Quickly answers "are any of my watched
 * amendments adopted yet?"
 *
 * Optional params:
 *   - bill_ref: filter to a single bill (e.g. only show amendments on
 *     119-hr-3838 to make this an NDAA-only tracker)
 *   - limit: max rows to render in the widget (default 12)
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

type Status = "adopted" | "rejected" | "withdrawn" | "reported" | "pending" | "unknown";

interface Watch {
  amendment_ref: string;
  bill_ref: string;
  note: string | null;
  status: Status;
  chamber: string | null;
  purpose: string | null;
  sponsor: { bioguide_id: string; name: string; party: string; state: string } | null;
  latest_action: { date: string | null; text: string | null };
}

const STATUS_COLOR: Record<Status, string> = {
  adopted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  withdrawn: "bg-stone-200 text-stone-700",
  reported: "bg-sky-100 text-sky-800",
  pending: "bg-amber-100 text-amber-800",
  unknown: "bg-stone-100 text-stone-600",
};

function formatBillRef(ref: string): string {
  const m = ref.match(/^(\d+)-(hr|s|hres|sres|hjres|sjres|hconres|sconres)-(\d+)$/);
  if (!m) return ref;
  const map: Record<string, string> = {
    hr: "H.R.", s: "S.", hres: "H.Res.", sres: "S.Res.",
    hjres: "H.J.Res.", sjres: "S.J.Res.", hconres: "H.Con.Res.", sconres: "S.Con.Res.",
  };
  return `${map[m[2]] || m[2]} ${m[3]}`;
}

export function WatchedAmendments({ spec, workspaceId }: WidgetProps) {
  const billRef = spec.params?.bill_ref as string | undefined;
  const limit = (spec.params?.limit as number) ?? 12;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "watched_amendments", workspaceId, billRef ?? "all"],
    queryFn: async () => {
      const url = billRef
        ? `/api/v1/workspaces/${workspaceId}/amendment-watches?bill_ref=${encodeURIComponent(billRef)}`
        : `/api/v1/workspaces/${workspaceId}/amendment-watches`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load watches");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Watched amendments could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" aria-live="polite" className="space-y-2"><span className="sr-only">Loading…</span><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-3/5" /></div>;
  const watches: Watch[] = data?.watches ?? [];
  const byStatus = (data?.by_bill ?? []).reduce(
    (acc: Record<string, number>, b: any) => {
      for (const k of Object.keys(b.by_status)) {
        acc[k] = (acc[k] ?? 0) + b.by_status[k];
      }
      return acc;
    },
    { adopted: 0, rejected: 0, pending: 0, reported: 0, withdrawn: 0, unknown: 0 } as Record<string, number>
  );

  if (watches.length === 0) {
    return (
      <div className="text-xs text-stone-600">
        No watched amendments yet. Open a bill's Amendments tab and star the ones you want to track, or ask your AI client to watch an amendment.
      </div>
    );
  }

  return (
    <div className="text-xs space-y-2">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-1.5">
        <div className="text-stone-700 font-semibold">{data.count} watched</div>
        <div className="flex gap-1.5">
          {(["adopted", "rejected", "pending", "reported", "withdrawn", "unknown"] as Status[]).map((s) =>
            byStatus[s] > 0 ? (
              <span key={s} className={`px-1.5 py-0.5 rounded ${STATUS_COLOR[s]} text-[10px] font-bold uppercase tracking-wide`}>
                {byStatus[s]} {s.slice(0, 3)}
              </span>
            ) : null
          )}
        </div>
      </div>
      <ul className="space-y-1.5">
        {watches.slice(0, limit).map((w) => (
          <li key={w.amendment_ref} className="flex items-start gap-2 text-[12px]">
            <Star size={12} aria-hidden="true" className="mt-0.5 shrink-0 fill-amber-400 text-amber-500" />
            <Link
              to={`/bills/${encodeURIComponent(w.bill_ref)}`}
              className="font-mono text-[11px] text-action hover:underline shrink-0 w-32 truncate"
              title={`${formatBillRef(w.bill_ref)} · ${w.amendment_ref}`}
            >
              {w.amendment_ref}
            </Link>
            {w.sponsor && (
              <span className={`font-bold w-3 text-center shrink-0 ${
                w.sponsor.party?.[0] === "R" ? "text-red-700" :
                w.sponsor.party?.[0] === "D" ? "text-action" : "text-amber-700"
              }`}>
                {(w.sponsor.party || "?").charAt(0)}
              </span>
            )}
            <span className="flex-1 text-stone-700 truncate" title={w.purpose ?? w.note ?? ""}>
              {(w.purpose || w.note || w.sponsor?.name || w.amendment_ref).slice(0, 80)}
            </span>
            <span className={`tier-tag text-[9px] shrink-0 ${STATUS_COLOR[w.status] ?? STATUS_COLOR.unknown}`}>
              {w.status}
            </span>
          </li>
        ))}
      </ul>
      {watches.length > limit && (
        <div className="text-[10px] text-stone-400 italic pt-1">
          +{watches.length - limit} more — open a bill's Amendments tab to see all.
        </div>
      )}
    </div>
  );
}
